import type { MangaItem } from '../types';
import type { SeriesGroup, StandaloneItem, DisplayEntry } from '../types/manga';

// 巻数表記パターン（順番重要: より具体的なものを先に）
// 末尾のみでなく、タイトル途中のどこにあってもマッチする
const VOLUME_PATTERNS: RegExp[] = [
  /\s+第\d+巻/,                         // スペース + 第1巻
  /\s+第[一二三四五六七八九十百千]+巻/, // スペース + 第一巻（漢数字）
  /\s+\d+巻/,                           // スペース + 1巻
  /\s+[（(]\d+[）)]/,                   // スペース + (1)、（1）
  /\s+[Vv]ol\.?\s*\d+/,                // スペース + Vol.1, Vol 1, vol.1
  /\s+[Vv]olume\s*\d+/,                // スペース + Volume 1
  /\s+#\d+/,                            // スペース + #1
  /\s+[\d１２３４５６７８９０]+/,       // スペース + 半角・全角数字
  /[\d１２３４５６７８９０]+(?=\s|—|─|-|$)/, // スペースなし数字: 直後に区切り文字か末尾
];

/**
 * タイトルから巻数表記を検出してそれより前の部分を返す
 * 巻数がない場合はタイトル全体を返す
 *
 * 例:
 *   'ベストマッチカノジョ2 -清楚JK...' → 'ベストマッチカノジョ'
 *   'タイトル 1巻 サブタイトル' → 'タイトル'
 *   '独立作品' → '独立作品'
 */
export function extractBaseTitle(title: string): string {
  const base = title.trim();

  for (const pattern of VOLUME_PATTERNS) {
    const match = base.match(pattern);
    if (match && match.index !== undefined) {
      // マッチした位置より前の部分がベースタイトル
      return base.substring(0, match.index).trim();
    }
  }

  return base;
}

/**
 * シリーズを一意に識別するキーを生成する
 * フォーマット: "作者名::ベースタイトル"
 */
export function getSeriesKey(author: string, title: string): string {
  return `${author}::${extractBaseTitle(title)}`;
}

/**
 * MangaItem の配列をシリーズ単位にグループ化する
 * - 同じ (author, baseTitle) を持つアイテムが2件以上 → SeriesGroup
 * - 1件のみ → StandaloneItem
 */
export function groupIntoSeries(items: MangaItem[]): DisplayEntry[] {
  // author::baseTitle → MangaItem[] のマップを構築
  const groups = new Map<string, MangaItem[]>();
  for (const item of items) {
    const key = getSeriesKey(item.author, item.title);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  const entries: DisplayEntry[] = [];
  for (const [key, groupItems] of groups) {
    if (groupItems.length >= 2) {
      // タイトル昇順（日本語ロケール）でソート
      const sorted = [...groupItems].sort((a, b) =>
        a.title.localeCompare(b.title, 'ja')
      );
      // 代表アイテム: 最高レーティング、同率は先頭巻
      const representative = sorted.reduce((best, v) =>
        v.rating > best.rating ? v : best,
        sorted[0]
      );
      const baseTitle = extractBaseTitle(sorted[0].title);
      const seriesGroup: SeriesGroup = {
        type: 'series',
        seriesKey: key,
        baseTitle,
        author: sorted[0].author,
        author_reading: sorted[0].author_reading,
        volumeCount: sorted.length,
        volumes: sorted,
        representativeItem: representative,
      };
      entries.push(seriesGroup);
    } else {
      const standalone: StandaloneItem = {
        type: 'standalone',
        item: groupItems[0],
      };
      entries.push(standalone);
    }
  }

  return entries;
}
